import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Check, LayoutGrid, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import PostThreadView from "./PostThreadView";
import { CatchUpBriefing } from "./CatchUpCard";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgFilter } from "@/scripts/OrgFilterContext";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  boardHrefForCard,
  parsePostPermalinkSearch,
  permalinkForPost,
} from "@/lib/permalinks";
import {
  entitlementMessageFor,
  getEntitlementCode,
  toastEntitlementOrError,
} from "@/lib/entitlementToast";
import { messageForCatchupGenerateError } from "@/lib/catchupGenerateError";

/**
 * Canonical workplace post/thread page: /o/:orgId/p/:postId
 */
const PostPermalinkPage = () => {
  const { orgId, postId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { setSelectedOrgId } = useOrgFilter();

  const [post, setPost] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [orgNameContext, setOrgNameContext] = useState(null);
  const [orgMembersMap, setOrgMembersMap] = useState({});
  const [canManageSettings, setCanManageSettings] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [entityBriefing, setEntityBriefing] = useState(null);
  const [summarizeMeta, setSummarizeMeta] = useState(null);
  const [summarizeError, setSummarizeError] = useState(null);

  const { fromCatchup, returnPath } = parsePostPermalinkSearch(location.search);
  const returnPathForBriefing = `${location.pathname}${location.search || ""}`;

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/user/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        });
        if (!cancelled) {
          setCurrentUserId(data?.user_id || data?.userId || null);
        }
      } catch {
        /* non-fatal for read-only viewing */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken || !orgId) return;
    let cancelled = false;
    const options = {
      headers: { Authorization: `Bearer ${accessToken}` },
      withCredentials: true,
    };
    (async () => {
      const [orgResult, membersResult, meResult] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/org/${orgId}`, options),
        axios.get(`${API_BASE_URL}/org/${orgId}/members`, options),
        axios.get(`${API_BASE_URL}/org/${orgId}/me`, options),
      ]);
      if (cancelled) return;
      if (orgResult.status === "fulfilled") {
        setOrgNameContext(orgResult.value.data?.name || null);
      }
      if (membersResult.status === "fulfilled") {
        const map = {};
        const members = Array.isArray(membersResult.value.data)
          ? membersResult.value.data
          : [];
        members.forEach((membership) => {
          const userId =
            membership.user?.user_id ||
            membership.user?.userId ||
            membership.user_id;
          if (userId) {
            map[userId] = membership.title || null;
          }
        });
        setOrgMembersMap(map);
      }
      if (meResult.status === "fulfilled") {
        const caps = meResult.value.data || {};
        setCanManageSettings(
          Boolean(
            caps.can_manage_settings ||
              caps.canManageSettings ||
              caps.is_owner ||
              caps.isOwner
          )
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId]);

  const loadPost = useCallback(async () => {
    if (!accessToken || !orgId || !postId) return;
    setLoading(true);
    setNotFound(false);
    setEntityBriefing(null);
    setSummarizeMeta(null);
    setSummarizeError(null);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/posts/${postId}`, {
        params: { org_id: orgId },
        headers: { Authorization: `Bearer ${accessToken}` },
        withCredentials: true,
      });
      setPost(data);
    } catch (err) {
      console.error("Failed to load permalink post:", err);
      setPost(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId, postId]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  const runEntitySummarize = async ({ force }) => {
    if (!accessToken || !orgId || !postId || summarizing) return;
    setSummarizing(true);
    setSummarizeError(null);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/catchup/generate`,
        {
          scope: "entity",
          post_id: Number(postId),
          force: force === true,
        },
        {
          params: { org_id: orgId },
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        }
      );
      const briefing = data?.summary?.summary_json || null;
      setEntityBriefing(briefing);
      setSummarizeMeta({
        fromCache: Boolean(data?.from_cache),
        reason: data?.reason || null,
        generatedAt: data?.summary?.generated_at || null,
      });
    } catch (err) {
      if (getEntitlementCode(err)) {
        const msg = entitlementMessageFor(err, { canManageSettings });
        setSummarizeError(msg);
        toastEntitlementOrError(err, "Failed to summarize thread.", {
          canManageSettings,
          orgId,
        });
      } else {
        setSummarizeError(
          messageForCatchupGenerateError(err, {
            fallback: "Failed to summarize thread.",
          })
        );
      }
    } finally {
      setSummarizing(false);
    }
  };

  const handleSummarize = () => runEntitySummarize({ force: false });
  const handleUpdateSummary = () => runEntitySummarize({ force: true });

  const orgName =
    post?.org?.name ||
    post?.team?.organization?.name ||
    post?.team?.org?.name ||
    orgNameContext ||
    orgId;
  const teamId = post?.teamId ?? post?.team_id ?? post?.team?.team_id ?? null;
  const teamName = post?.team?.name || post?.teamName || post?.team_name || null;
  const isCard = Boolean(post?.kanbanStatus || post?.kanban_status);
  const contextLine = teamName
    ? `${orgName} · ${teamName}`
    : `${orgName} · Org-wide`;

  const boardHref =
    isCard && teamId
      ? boardHrefForCard({
          orgId,
          teamId,
          postId,
          sprintId: post?.sprint_id ?? post?.sprintId ?? null,
        })
      : null;

  const canonicalPath = permalinkForPost({ orgId, postId });

  const feedHref = teamId
    ? `/teams/${encodeURIComponent(String(teamId))}?org_id=${encodeURIComponent(String(orgId))}&tab=feed`
    : "/dashboard";

  const returnIsDashboard =
    returnPath &&
    (returnPath === "/dashboard" || returnPath.startsWith("/dashboard?"));
  const returnIsOrg =
    returnPath &&
    (returnPath.startsWith("/orgs/") || returnPath.startsWith("/orgs?"));
  const returnIsTeam =
    returnPath &&
    (returnPath.startsWith("/teams/") || returnPath.startsWith("/teams?"));

  const backLabel = fromCatchup && returnPath
    ? "Back to AI summary"
    : returnIsDashboard
      ? "Back to Dashboard"
      : returnIsTeam && teamName
        ? `Back to ${teamName}`
        : returnIsOrg && orgName
          ? `Back to ${orgName}`
          : returnPath
            ? "Back"
            : teamName
              ? `Back to ${teamName}`
              : orgName
                ? `Back to ${orgName}`
                : "Back to feed";

  const handleCopyLink = async () => {
    if (!canonicalPath) return;
    const url = `${window.location.origin}${canonicalPath}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  // Prefer explicit return= (dashboard / catch-up / etc.); else contextual feed
  const handleBack = () => {
    if (returnPath) {
      navigate(returnPath);
      return;
    }
    if (!teamId && orgId) {
      setSelectedOrgId(orgId);
    }
    navigate(feedHref);
  };

  if (!accessToken) {
    return null;
  }

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground text-center">
        Loading thread…
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="p-6 space-y-4 max-w-lg mx-auto">
        <h1 className="text-xl font-semibold">Post not found</h1>
        <p className="text-sm text-muted-foreground">
          This post may not exist, or you do not have access to it.
        </p>
        <Button type="button" variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 flex flex-col items-center pb-8">
      <div className="w-full max-w-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-3">
          <div className="min-w-0 space-y-0.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 -ml-2"
              onClick={handleBack}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {backLabel}
            </Button>
            <p className="text-xs text-muted-foreground pl-1">{contextLine}</p>
          </div>
          <div className="flex flex-wrap gap-1">
            {!entityBriefing ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-muted-foreground"
                disabled={summarizing}
                onClick={handleSummarize}
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                {summarizing
                  ? "Summarizing…"
                  : isCard
                    ? "Summarize"
                    : "Summarize"}
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-muted-foreground"
                disabled={summarizing}
                onClick={handleUpdateSummary}
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                {summarizing ? "Updating…" : "Update"}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-muted-foreground"
              onClick={handleCopyLink}
            >
              {copied ? (
                <Check className="h-4 w-4 mr-1.5" />
              ) : (
                <Copy className="h-4 w-4 mr-1.5" />
              )}
              {copied ? "Copied" : "Copy"}
            </Button>
            {boardHref ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-muted-foreground"
                asChild
              >
                <Link to={boardHref}>
                  <LayoutGrid className="h-4 w-4 mr-1.5" />
                  Board
                </Link>
              </Button>
            ) : null}
          </div>
        </div>

        {(entityBriefing || summarizeError) && (
          <div className="space-y-2 border-b border-border/50 pb-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground">
                {isCard ? "Card summary" : "Thread summary"}
              </h3>
              {summarizeMeta && !summarizeError ? (
                <p className="text-xs text-muted-foreground">
                  {summarizeMeta.fromCache || summarizeMeta.reason === "unchanged"
                    ? "Cached"
                    : "Just generated"}
                  {summarizeMeta.generatedAt
                    ? ` · ${new Date(
                        summarizeMeta.generatedAt
                      ).toLocaleString()}`
                    : ""}
                </p>
              ) : null}
            </div>
            {summarizeError ? (
              <p className="text-sm text-destructive" role="alert">
                {summarizeError}
              </p>
            ) : (
              <CatchUpBriefing
                briefing={entityBriefing}
                returnPath={returnPathForBriefing}
                orgId={orgId}
                showThreadLinks={false}
              />
            )}
          </div>
        )}

        <PostThreadView
          variant="page"
          hidePageHeader
          post={post}
          isOpen
          onClose={handleBack}
          accessToken={accessToken}
          currentUserId={currentUserId}
          orgId={orgId}
          orgName={orgName}
          orgMembersMap={orgMembersMap}
          onPostUpdate={(updated) => setPost(updated)}
        />
      </div>
    </div>
  );
};

export default PostPermalinkPage;
